<template>
  <div class="contact-persons">
    <!-- title & description -->
    <form class="contact-persons__header">
      <div class="contact-persons__header-image">
        <!-- @todo we dont have any field, just an API, so quickest (and cringiest) is to place it in src/images -->
        <responsive-image :images="headerImages()" />
      </div>
      <div class="container contact-persons__header-container">
        <div class="contact-persons__header-content">
          <slot name="title"></slot>
          <slot name="description"></slot>
          <div class="contact-persons__select-wrapper form-type-select-wrapper">
            <select
              id="contact-persons-country"
              class="form-select"
              :class="{ 'form-select--selected': country }"
              v-model="country">
              <option value="">{{ t.contactPersons.selectCountry }}</option>
              <option
                v-for="countryOption in availableCountries"
                :key="countryOption.id"
                :selected="countryOption.id === country"
                :value="countryOption.id">
                {{ countryOption.label }}
              </option>
            </select>
            <div class="select-additional"></div>
            <label
              for="contact-persons-country"
              v-text="t.contactPersons.selectCountry"></label>
          </div>
          <button
            v-if="$slots.link"
            type="button"
            class="content-link contact-persons__link"
            @click="scrollToContent">
            <slot name="link"></slot>
          </button>
        </div>
      </div>
    </form>
    <div class="container">
      <div ref="scrollTarget" class="contact-persons__content">
        <!-- filters (tabs) -->
        <div v-if="!loading" class="contact-persons__filters-wrapper">
          <h2
            class="contact-persons__filters-heading"
            v-text="t.contactPersons.availableContacts"></h2>
          <div class="contact-persons__filters tabs">
            <div class="tabs__navigation tabs__navigation--spaceless">
              <div
                ref="viewport"
                class="tabs__viewport tabs__navigation-viewport">
                <ul
                  class="tabs__navigation-container tabs__container contact-persons__tabs-container">
                  <li
                    v-for="filter in availableCategories"
                    :key="filter.id"
                    :class="{
                      'tabs__navigation-item--active':
                        activeCategory === filter.id,
                    }"
                    @click="setActiveCategory(filter.id)"
                    class="tabs__navigation-item">
                    <button class="tabs__navigation-link">
                      {{ filter.label }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <loading v-if="loading"></loading>
        <div v-else>
          <ul
            v-if="filteredPersons.length > 0"
            class="contact-persons__results"
            name="contact-persons-list"
            tag="ul">
            <li
              v-for="person in filteredPersons"
              :key="person.id"
              v-html="person.render.teaser"
              @click="(e) => openModal(e, person.id)"
              class="li__person"></li>
          </ul>
          <div v-else class="contact-persons__empty text-formatted">
            <p>{{ t.contactPersons.noResults }}</p>
          </div>
        </div>
      </div>
    </div>
    <div ref="modal" class="modal modal--salesperson-contact">
      <div class="modal__header">
        <h4 v-text="t.contactPersons.modalTitle" />
        <button class="modal__close" :title="t.contactPersons.modalClose">
          <i class="fa-solid fa-sharp fa-xmark" aria-hidden="true"></i>
          <span class="visually-hidden" v-text="t.contactPersons.modalClose" />
        </button>
      </div>

      <div class="modal__content">
        <div v-if="currentPerson" class="salesperson-contact__card">
          <responsive-image
            class="salesperson-contact__card-image"
            :images="currentPersonImage" />
          <div class="salesperson-contact__card-wrapper">
            <div class="salesperson-contact__card-text-wrapper">
              <p
                class="salesperson-contact__card-job-title"
                v-text="currentPerson.job_title" />
              <h4
                class="salesperson-contact__card-name"
                v-text="
                  `${currentPerson.first_name} ${currentPerson.last_name}`
                " />
            </div>
            <div
              v-if="currentPerson.social_media"
              class="person__socials person__socials--no-spacing">
              <a
                v-if="currentPerson.social_media.whatsapp"
                class="person__social person__social--whatsapp"
                :href="currentPerson.social_media.whatsapp">
                <i class="fa-brands fa-whatsapp" title="WhatsApp"></i>
              </a>

              <a
                v-if="currentPerson.social_media.linkedin"
                class="person__social person__social--linkedin"
                :href="currentPerson.social_media.linkedin">
                <i class="fa-brands fa-linkedin-in" title="LinkedIn"></i>
              </a>

              <a
                v-if="currentPerson.social_media.telegram"
                class="person__social person__social--telegram"
                :href="currentPerson.social_media.telegram">
                <i class="fa-brands fa-telegram" title="Telegram"></i>
              </a>

              <a
                v-if="currentPerson.social_media.vk"
                class="person__social person__social--vk"
                :href="currentPerson.social_media.vk">
                <i class="fa-brands fa-vk" title="VK"></i>
              </a>
            </div>
          </div>
        </div>
        <div class="salesperson-contact__form" v-html="webform" />
      </div>
    </div>
  </div>
</template>

<script>
// components
import EmblaCarousel from 'embla-carousel';
import Loading from './Loading';
import ResponsiveImage from './ResponsiveImage';
// utils
import translationStrings from '../utils/translation-strings';
import { getContactPersons } from '../utils/api';
import { toggleModal } from '../utils/modal';
import { updateSelects, updateTextareas } from '../elements/form';

export default {
  name: 'contact-persons',
  components: {
    Loading,
    ResponsiveImage,
  },
  props: {
    lang: {
      type: String,
      required: true,
    },
    webform: {
      type: String,
      required: true,
    },
    directory: {
      type: String,
    },
  },
  data() {
    return {
      country: null,
      filters: {
        country: [],
        person_category: [],
      },
      activeCategory: -1,
      loading: true,
      tabsNavigation: null,
      persons: [],
      currentPerson: null,
      t: translationStrings,
    };
  },

  computed: {
    availableCountries() {
      const personCountries = [
        ...new Set(
          this.persons.reduce((accumulator, current) => {
            accumulator.push(...current.country);
            return accumulator;
          }, []),
        ),
      ];

      return this.filters.country.filter((country) =>
        personCountries.includes(country.id),
      );
    },
    availableCategories() {
      const personCategories = [
        ...new Set(
          this.persons.reduce((accumulator, current) => {
            accumulator.push(...current.category);
            return accumulator;
          }, []),
        ),
      ];

      return [
        ...[{ id: -1, label: this.t.contactPersons.availableContacts }],
        ...this.filters.person_category.filter((country) =>
          personCategories.includes(country.id),
        ),
      ];
    },
    islistingAllCategories() {
      return this.activeCategory === -1;
    },
    filteredPersons() {
      // no need to filter unecessarily
      if (this.islistingAllCategories && !this.country) {
        return this.persons;
      }

      return this.persons.filter((person) => {
        let included = true;

        if (this.country && !person.country.includes(this.country)) {
          included = false;
        }

        if (
          !this.islistingAllCategories &&
          !person.category.includes(this.activeCategory)
        ) {
          included = false;
        }

        return included;
      });
    },
    currentPersonImage() {
      if (!this.currentPerson) {
        return;
      }

      if (!Object.keys(this.currentPerson.image).length) {
        return [
          {
            src: {
              '#markup': `/${this.directory}/src/images/bemi-sales-contacts-placeholder.svg`,
            },
            alt: { '#markup': this.t.contactPersons.contact },
          },
        ];
      }

      return this.currentPerson.image;
    },
  },
  async mounted() {
    await this.fetchData();
    // update the field labels
    const selects = [...this.$refs.modal.querySelectorAll('form select')];
    const textareas = [...this.$refs.modal.querySelectorAll('textarea')];

    if (selects.length) {
      updateSelects(selects);
    }

    if (textareas.length) {
      updateTextareas(textareas);
    }
  },
  updated() {
    // just to really make sure elements are ready
    if (!this.loading) {
      if (this.tabsNavigation == null) {
        this.buildTabsNavigation();
      }
    }
  },
  methods: {
    async fetchData() {
      try {
        const { data } = await getContactPersons(this.lang);
        this.persons = data.persons;
        this.filters = data.filters;
      } catch ({ response }) {
        // allow console log so we can see at least some error message
        // eslint-disable-next-line no-console
        console.log('❗ Something went wrong.', response);
      } finally {
        this.loading = false;
      }
    },
    buildTabsNavigation() {
      this.tabsNavigation = EmblaCarousel(this.$refs.viewport, {
        breakpoints: {
          '(min-width: 0px)': {
            watchDrag: true,
          },
          '(min-width: 1181px)': {
            watchDrag: false,
          },
        },
        containScroll: 'trimSnaps',
        inViewThreshold: 0.99,
      });

      this.tabsNavigation.on('init', () => {
        const slidesThumbs = this.tabsNavigation.slideNodes();

        if (slidesThumbs !== null) {
          const scrollToIndex = slidesThumbs.map(
            (_, index) => () => this.tabsNavigation.scrollTo(index),
          );

          slidesThumbs.forEach((slideNode, index) => {
            slideNode.addEventListener('click', scrollToIndex[index], false);
          });
        }
      });
    },
    headerImages() {
      const header = [
        { media: '1181', bp: 'desktop' },
        { media: '421', bp: 'tablet' },
        { media: '0', bp: 'mobile' },
        { bp: 'desktop', alt: true },
      ];

      return header.map((item) => {
        return {
          media: item.media
            ? { '#markup': `(min-width: ${item.media}px)` }
            : null,
          src: {
            '#markup': `/${this.directory}/src/images/bemi-contact-persons-${item.bp}-placeholder.webp`,
          },
          alt: item.alt ? { '#markup': this.t.contactPersons.contact } : null,
        };
      });
    },
    setActiveCategory(activeCategoryId) {
      this.activeCategory = activeCategoryId;
    },
    scrollToContent() {
      this.$refs.scrollTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    openModal(event, personId) {
      // We only want to open the modal when the action button is clicked.
      if (
        !(
          event.target?.classList?.contains('person__email') ||
          event.target?.parentNode?.classList?.contains('person__email')
        )
      ) {
        return;
      }

      this.currentPerson = this.persons.find(
        (person) => person.id === personId,
      );

      this.$refs.modal.querySelector('input[name="person_id"]').value =
        personId;
      toggleModal(this.$refs.modal, event.currentTarget, true);
    },
  },
};
</script>
